/**
 * FriendStatsHeader Component
 * Animated VS header with colored ring avatars
 */

import React, { useRef, useCallback, useMemo } from 'react';
import { View, Text, Animated, Platform, useWindowDimensions } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Crown } from 'lucide-react-native';
import LottieView from 'lottie-react-native';
import * as Haptics from 'expo-haptics';
import type { FriendStatsSummary } from '../../../src/friends/types/friends.types';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { useUserAvatar } from '../../../src/avatars/hooks/useUserAvatar';
import { getUserName } from '../../../src/friends/helpers/getUserName.helper';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { FriendAvatar } from '../shared/FriendAvatar';
import { VS_LOTTIE_SOURCE } from '../../../constants/assets';
import { usePlayerRanks } from '../../../src/economy/hooks/usePlayerRanks';
import { PlayerRankBadge } from '../../../src/economy/components/PlayerRankBadge/PlayerRankBadge';
import { PlayerLevelPill } from '../../../src/economy/components/PlayerLevelPill/PlayerLevelPill';
import {
  getFriendStatsHeaderStyles,
  getVsLottieSize,
  getVsCenterDynamicStyles,
  H2H_CREST_SIZE,
} from './FriendStatsHeader.styles';
import { useTheme } from '../../../src/theme/useTheme';

const HEADER_AVATAR_SIZE = 84;

/** Persists "has animated" per friendId across remounts so the entrance animation runs only once per visit. */
const hasAnimatedForFriend = new Map<string, boolean>();

interface FriendStatsHeaderProps {
  summary: FriendStatsSummary;
  testID?: string;
}

export const FriendStatsHeader: React.FC<FriendStatsHeaderProps> = ({ summary, testID }) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const styles = useMemo(() => getFriendStatsHeaderStyles(theme), [theme]);
  const vsSize = useMemo(() => getVsLottieSize(windowWidth), [windowWidth]);
  const vsCenterStyles = useMemo(() => getVsCenterDynamicStyles(vsSize), [vsSize]);
  const { user } = useSupabaseAuth();
  const fetchedUserAvatar = useUserAvatar();
  // Prefer live profile (refetched on focus) so avatar updates when changed in menu; fallback to summary to avoid flash on first load
  const userAvatarId = fetchedUserAvatar ?? summary.user?.avatarId;
  const userName = getUserName(user);
  const userIsLeading = summary.userWins > summary.friendWins;
  const friendIsLeading = summary.friendWins > summary.userWins;

  const friendId = summary.friend.id;

  // Only two players here, so the hook lives in the component rather than the screen
  // (specs/rank-on-social-screens AC-8). Guests are signed out → query disabled → no crests.
  const rankIds = useMemo(
    () => [user?.id, friendId].filter((id): id is string => id != null && id !== ''),
    [user?.id, friendId]
  );
  const { getPlayerRank } = usePlayerRanks(rankIds);
  const userRank = getPlayerRank(user?.id);
  const friendRank = getPlayerRank(friendId);
  const userAvatarAnim = useRef(new Animated.Value(-200)).current;
  const friendAvatarAnim = useRef(new Animated.Value(200)).current;
  const vsOpacity = useRef(new Animated.Value(0)).current;
  const isAnimatingRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      if (hasAnimatedForFriend.get(friendId)) {
        // Already animated – set final positions so avatars are visible on remount
        userAvatarAnim.setValue(0);
        friendAvatarAnim.setValue(0);
        vsOpacity.setValue(1);
        return;
      }
      if (isAnimatingRef.current) return;
      isAnimatingRef.current = true;
      hasAnimatedForFriend.set(friendId, true);

      userAvatarAnim.setValue(-200);
      friendAvatarAnim.setValue(200);
      vsOpacity.setValue(0);

      Animated.parallel([
        Animated.spring(userAvatarAnim, {
          toValue: 0,
          tension: 40,
          friction: 7,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.spring(friendAvatarAnim, {
          toValue: 0,
          tension: 40,
          friction: 7,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(vsOpacity, {
          toValue: 1,
          duration: 400,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]).start(() => {
        isAnimatingRef.current = false;
        if (Platform.OS !== 'web') {
          Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => undefined);
        }
      });
    }, [friendId, userAvatarAnim, friendAvatarAnim, vsOpacity])
  );

  return (
    <View style={styles.vsSection} testID={testID || 'friend-stats-header'}>
      {/* User Avatar Column */}
      <Animated.View
        style={[
          styles.vsAvatarColumn,
          { transform: [{ translateX: userAvatarAnim }] },
        ]}
      >
        <View style={styles.vsAvatarRingUser}>
          <FriendAvatar
            avatarId={(userAvatarId ?? undefined) as DisplayAvatarId | undefined}
            avatarConfig={summary.user?.avatarConfig}
            small={false}
            size={HEADER_AVATAR_SIZE}
            nameForInitials="You"
            style={styles.vsAvatarInner}
          />
          {userIsLeading && (
            <View style={styles.vsCrownBadge}>
              <Crown size={18} color={theme.warning ?? '#FFD700'} />
            </View>
          )}
          {userRank != null && (
            <PlayerRankBadge
              rankIndex={userRank.rank}
              size={H2H_CREST_SIZE}
              style={styles.vsRankCrest}
            />
          )}
        </View>
        <Text style={styles.vsPlayerName} numberOfLines={1}>{userName}</Text>
        {userRank != null && (
          <PlayerLevelPill level={userRank.level} size="md" style={styles.vsRankLevelPill} />
        )}
      </Animated.View>

      {/* VS: Lottie plays once on load, size scales with screen */}
      <Animated.View style={[styles.vsTextCenter, vsCenterStyles.vsTextCenter, { opacity: vsOpacity }]}>
        <LottieView
          source={VS_LOTTIE_SOURCE}
          style={vsCenterStyles.vsLottie}
          loop={false}
          autoPlay
        />
      </Animated.View>

      {/* Friend Avatar Column */}
      <Animated.View
        style={[
          styles.vsAvatarColumn,
          { transform: [{ translateX: friendAvatarAnim }] },
        ]}
      >
        <View style={styles.vsAvatarRingFriend}>
          <FriendAvatar
            avatarId={(summary.friend.avatarId ?? undefined) as DisplayAvatarId | undefined}
            avatarConfig={summary.friend.avatarConfig}
            fallbackUrl={summary.friend.avatar ?? undefined}
            small={false}
            size={HEADER_AVATAR_SIZE}
            nameForInitials={summary.friend.name}
            style={styles.vsAvatarInner}
          />
          {friendIsLeading && (
            <View style={styles.vsCrownBadge}>
              <Crown size={18} color={theme.warning ?? '#FFD700'} />
            </View>
          )}
          {friendRank != null && (
            <PlayerRankBadge
              rankIndex={friendRank.rank}
              size={H2H_CREST_SIZE}
              style={styles.vsRankCrest}
            />
          )}
        </View>
        <Text style={styles.vsPlayerName} numberOfLines={1}>{summary.friend.name}</Text>
        {friendRank != null && (
          <PlayerLevelPill level={friendRank.level} size="md" style={styles.vsRankLevelPill} />
        )}
      </Animated.View>
    </View>
  );
};
